
// ---- Bucket integration ----
const initLegacyBucket = require('./legacy-bucket-integration.js');

// Use localStorage theme if available, otherwise fall back to server-provided window.Theme
let currentTheme;
try {
  const storedTheme = localStorage.getItem('currentTheme');
  if (storedTheme) {
    currentTheme = JSON.parse(storedTheme);
  }
} catch (e) {}
if (!currentTheme) {
  currentTheme = window.Theme;
}

const themeColors = Object.values(currentTheme.data)
const themeRgb = themeColors.map((el) => {
    return { r: parseInt(el.slice(1, 3), 16), g: parseInt(el.slice(3, 5), 16), b: parseInt(el.slice(5, 7), 16) }
})

window.themeColors = themeColors
window.themeRgb = themeRgb
window.currentTheme = currentTheme

// Listen for theme changes
window.addEventListener('themeChanged', (e) => {
  const newTheme = e.detail || currentTheme;
  const newColors = Object.values(newTheme.data);
  const newRgb = newColors.map((el) => {
    return { r: parseInt(el.slice(1, 3), 16), g: parseInt(el.slice(3, 5), 16), b: parseInt(el.slice(5, 7), 16) }
  });
  window.themeColors = newColors;
  window.themeRgb = newRgb;
  window.currentTheme = newTheme;
});

const PromptGenerator = require('../common/promptgenerator');


function drawEmoji(emoji, contrast = 5, luminosity = 25) {
    const canvas = document.createElement('canvas');
    canvas.width = 24;
    canvas.height = 24;
    const ctx = canvas.getContext('2d');

    // Set the font and draw the emoji
    ctx.font = '20px serif';
    ctx.fillText(emoji, 0, 20);

    // Get the image data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;

    // Adjust contrast
    const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));
    for (let i = 0; i < pixels.length; i += 4) {
        const avg = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
        const contrastValue = factor * (avg - 128) + 128;
        pixels[i] = pixels[i + 1] = pixels[i + 2] = contrastValue;
    }

    // Adjust luminosity
    for (let i = 0; i < pixels.length; i += 4) {
        pixels[i] += luminosity; // Red
        pixels[i + 1] += luminosity; // Green
        pixels[i + 2] += luminosity; // Blue
    }

    // Apply theme colors when available
    const palette = Array.isArray(window.themeRgb) ? window.themeRgb : null;
    if (palette && palette.length) {
        const maxIdx = palette.length - 1;
        for (let i = 0; i < pixels.length; i += 4) {
            const grayscaleValue = Math.floor(pixels[i] / 17); // Dividing by 17 for 16 levels of gray
            const idx = Math.max(0, Math.min(maxIdx, grayscaleValue));
            pixels[i] = palette[idx].r;
            pixels[i + 1] = palette[idx].g;
            pixels[i + 2] = palette[idx].b;
        }
    }

    // Put the modified pixel data back to the canvas
    ctx.putImageData(imageData, 0, 0);

    return canvas;
}

class DashboardVideoPlayer {
    constructor(videoElementId) {
        this.video = document.getElementById(videoElementId);
        this.container = this.video.parentElement;
        this.isPlaying = false;
        this.volume = 1.0;
        this.isDragging = false;
        
        this.createPlayerControls();
        this.bindEvents();
    }

    createPlayerControls() {
        // Create video controls container
        const controlsContainer = document.createElement('div');
        controlsContainer.className = 'video-controls';
        controlsContainer.id = 'videoControls';
        
        const controlsHTML = `
            <div class="media-seek-bar" id="videoSeekBar">
                <div class="media-seek-fill" id="videoSeekFill"></div>
            </div>
            <div class="media-transport">
                <button class="media-transport-btn" id="videoPP" title="Play / Pause">▶</button>
                <button class="media-transport-btn" id="videoStop" title="Stop">■</button>
                <div class="media-time-display">
                    <span id="videoCurTime">0:00</span>
                    <span class="media-time-sep"> / </span>
                    <span id="videoDurTime">0:00</span>
                </div>
                <div class="media-volume">
                    <button class="media-vol-btn" id="mediaVideoVolBtn" title="Mute"><img src="" width="13" height="13" alt=""></button>
                    <input type="range" class="media-vol-slider" id="videoVol" min="0" max="100" step="1" value="100">
                </div>
                <select class="media-speed-select" id="videoSpeed">
                    <option value="0.25">0.25×</option>
                    <option value="0.5">0.5×</option>
                    <option value="0.75">0.75×</option>
                    <option value="1" selected>1×</option>
                    <option value="1.25">1.25×</option>
                    <option value="1.5">1.5×</option>
                    <option value="2">2×</option>
                </select>
            </div>
        `;
        
        controlsContainer.innerHTML = controlsHTML;
        this.container.appendChild(controlsContainer);
        
        this.controls = {
            seekBar: document.getElementById('videoSeekBar'),
            seekFill: document.getElementById('videoSeekFill'),
            playPauseBtn: document.getElementById('videoPP'),
            stopBtn: document.getElementById('videoStop'),
            currentTime: document.getElementById('videoCurTime'),
            duration: document.getElementById('videoDurTime'),
            volumeBtn: document.getElementById('mediaVideoVolBtn'),
            volumeSlider: document.getElementById('videoVol'),
            speedSelect: document.getElementById('videoSpeed')
        };

        // Initialize volume slider
        this.controls.volumeSlider.style.setProperty('--vol-pct', '100%');
        
        // Initialize themed volume icon
        this.updateVolumeIcon();
    }

    bindEvents() {
        // Play/Pause
        this.controls.playPauseBtn.addEventListener('click', () => {
            if (this.video.paused) {
                this.video.play();
                this.controls.playPauseBtn.textContent = '⏸';
                this.controls.playPauseBtn.classList.add('playing');
            } else {
                this.video.pause();
                this.controls.playPauseBtn.textContent = '▶';
                this.controls.playPauseBtn.classList.remove('playing');
            }
        });

        // Stop
        this.controls.stopBtn.addEventListener('click', () => {
            this.video.pause();
            this.video.currentTime = 0;
            this.controls.playPauseBtn.textContent = '▶';
            this.controls.playPauseBtn.classList.remove('playing');
            this.controls.seekFill.style.width = '0%';
            this.controls.currentTime.textContent = '0:00';
        });

        // Seek bar
        this.controls.seekBar.addEventListener('click', (e) => {
            if (!this.video.duration) return;
            const rect = this.controls.seekBar.getBoundingClientRect();
            this.video.currentTime = ((e.clientX - rect.left) / rect.width) * this.video.duration;
        });

        // Volume
        this.controls.volumeSlider.addEventListener('input', () => {
            this.video.muted = false;
            this.controls.volumeBtn.classList.remove('muted');
            this.setVolume(parseInt(this.controls.volumeSlider.value) / 100);
        });

        this.controls.volumeBtn.addEventListener('click', () => {
            this.toggleMute();
        });

        // Speed
        this.controls.speedSelect.addEventListener('change', () => {
            this.video.playbackRate = parseFloat(this.controls.speedSelect.value);
        });

        // Video events
        this.video.addEventListener('timeupdate', () => {
            if (!this.video.duration) return;
            const pct = (this.video.currentTime / this.video.duration) * 100;
            this.controls.seekFill.style.width = pct + '%';
            this.controls.currentTime.textContent = this.formatTime(this.video.currentTime);
        });

        this.video.addEventListener('loadedmetadata', () => {
            this.controls.duration.textContent = this.formatTime(this.video.duration);
            this.setVolume(this.volume);
        });

        this.video.addEventListener('ended', () => {
            this.controls.playPauseBtn.textContent = '▶';
            this.controls.playPauseBtn.classList.remove('playing');
            this.controls.seekFill.style.width = '100%';
        });

        // Drag functionality for seek bar
        this.controls.seekBar.addEventListener('mousedown', (e) => {
            this.isDragging = true;
            this.seek(e);
        });

        document.addEventListener('mousemove', (e) => {
            if (this.isDragging) {
                this.seek(e);
            }
        });

        document.addEventListener('mouseup', () => {
            this.isDragging = false;
        });

        // Touch support
        this.controls.seekBar.addEventListener('touchstart', (e) => {
            this.isDragging = true;
            this.seekTouch(e);
        });

        document.addEventListener('touchmove', (e) => {
            if (this.isDragging) {
                this.seekTouch(e);
            }
        });

        document.addEventListener('touchend', () => {
            this.isDragging = false;
        });
    }

    seek(e) {
        if (!this.video.duration) return;
        const rect = this.controls.seekBar.getBoundingClientRect();
        this.video.currentTime = ((e.clientX - rect.left) / rect.width) * this.video.duration;
    }

    seekTouch(e) {
        if (!this.video.duration) return;
        const rect = this.controls.seekBar.getBoundingClientRect();
        const touch = e.touches[0];
        this.video.currentTime = ((touch.clientX - rect.left) / rect.width) * this.video.duration;
    }

    setVolume(value) {
        this.volume = Math.max(0, Math.min(1, value));
        this.video.volume = this.volume;
        this.controls.volumeSlider.value = Math.round(this.volume * 100);
        this.controls.volumeSlider.style.setProperty('--vol-pct', Math.round(this.volume * 100) + '%');
        this.updateVolumeIcon();
    }

    toggleMute() {
        this.video.muted = !this.video.muted;
        this.controls.volumeBtn.classList.toggle('muted', this.video.muted);
        this.updateVolumeIcon();
    }

    updateVolumeIcon() {
        // Update volume icon based on state
        const emoji = this.video.muted || this.volume === 0 ? '🔇' : this.volume < 0.5 ? '🔉' : '🔊';
        const canvas = drawEmoji(emoji);
        const img = this.controls.volumeBtn.querySelector('img');
        if (img) {
            img.src = canvas.toDataURL();
        } else {
            const i = document.createElement('img');
            i.src = canvas.toDataURL();
            i.width = 13;
            i.height = 13;
            i.style.display = 'block';
            i.style.width = '13px';
            i.style.height = '13px';
            this.controls.volumeBtn.innerHTML = '';
            this.controls.volumeBtn.appendChild(i);
        }
    }

    formatTime(seconds) {
        if (isNaN(seconds)) return '0:00';
        
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    loadVideo(src) {
        this.video.src = src;
        this.video.load();
        this.video.playbackRate = 1.0;
        
        // Reset controls
        this.controls.playPauseBtn.textContent = '▶';
        this.controls.playPauseBtn.classList.remove('playing');
        this.controls.seekFill.style.width = '0%';
        this.controls.currentTime.textContent = '0:00';
        this.controls.duration.textContent = '0:00';
        this.controls.speedSelect.value = '1';
    }
}

class VideoGenForms {
    constructor() {
        this.promptGenerator = new PromptGenerator();
        this.models = {};
        this.currentModel = null;
        this.currentCategory = 'text-to-video';
        this.availableModels = [];
        this.isGenerating = false;
        this.promptGeneratorPools = this.promptGenerator.promptGeneratorPools;
        this.promptThemes = this.promptGenerator.promptThemes;
        this.promptThemeAliases = this.promptGenerator.promptThemeAliases;
        this.uploadedFileUrls = {};
        this.init();
    }

    buildPromptPool(partsA, partsB, formatter, limit = 2000) {
        return this.promptGenerator.buildPromptPool(partsA, partsB, formatter, limit);
    }

    createPromptGeneratorPools() {
        return this.promptGenerator.createPromptGeneratorPools();
    }

    createPromptThemes() {
        return this.promptGenerator.createPromptThemes();
    }
    createPromptThemeAliases() {
        return this.promptGenerator.createPromptThemeAliases();
    }
    getPromptThemeCatalog() {
        const aliases = Object.keys(this.promptThemeAliases || {});
        return aliases
            .filter((alias) => alias !== 'all')
            .sort((a, b) => a.localeCompare(b))
            .map((alias) => ({
                value: alias,
                label: alias.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
                themeKey: this.promptThemeAliases[alias]
            }));
    }

    resolvePromptThemeKey(value) {
        if (!value || value === 'all') {
            return null;
        }

        const themeKey = this.promptThemeAliases[value] || value;
        return this.promptThemes[themeKey] ? themeKey : null;
    }

    getPromptThemeOptionsHtml() {
        const options = ['<option value="all">All Themes</option>'];
        this.getPromptThemeCatalog().forEach((entry) => {
            options.push(`<option value="${entry.value}">${entry.label}</option>`);
        });
        return options.join('');
    }

    pickRandom(items) {
        if (!Array.isArray(items) || !items.length) {
            return '';
        }
        return items[Math.floor(Math.random() * items.length)];
    }

    pickRandomDistinct(items, count) {
        if (!Array.isArray(items) || !items.length) {
            return [];
        }

        const copy = [...items];
        for (let i = copy.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [copy[i], copy[j]] = [copy[j], copy[i]];
        }
        return copy.slice(0, Math.min(count, copy.length));
    }

    composeFieldWithThemes(fieldName, themeKeys = [], mixCount = 1, includeBase = false) {
        const baseValue = fieldName === 'exclude'
            ? this.getRandomExcludeValues()
            : this.getRandomPoolValue(fieldName);

        const themedParts = [];
        themeKeys.forEach((themeKey) => {
            const theme = this.promptThemes[themeKey];
            if (!theme || !Array.isArray(theme[fieldName])) {
                return;
            }

            const sample = this.pickRandom(theme[fieldName]);
            if (sample) {
                themedParts.push(sample);
            }
        });

        const selectedThemedParts = this.pickRandomDistinct(themedParts, Math.max(1, mixCount));

        if (fieldName === 'exclude') {
            if (!includeBase && selectedThemedParts.length) {
                return selectedThemedParts.slice(0, 2);
            }

            const baseItems = Array.isArray(baseValue) ? baseValue : [baseValue];
            return [...selectedThemedParts, ...baseItems].filter(Boolean).slice(0, 3);
        }

        if (!includeBase && selectedThemedParts.length) {
            return selectedThemedParts.join(', ');
        }

        return [...selectedThemedParts, baseValue].filter(Boolean).join(', ');
    }

    getPromptGeneratorOptions(builder) {
        return {
            theme: builder?.querySelector('.prompt-theme-select')?.value || 'all',
            mode: builder?.querySelector('.prompt-random-mode-select')?.value || 'themed'
        };
    }

    getThemeKeysForOptions(options = {}) {
        const resolvedTheme = this.resolvePromptThemeKey(options.theme);
        const allThemeKeys = Object.keys(this.promptThemes || {});

        if (!allThemeKeys.length) {
            return [];
        }

        if (options.mode === 'chaos') {
            if (resolvedTheme) {
                const others = allThemeKeys.filter((key) => key !== resolvedTheme);
                const mixed = this.pickRandomDistinct(others, 2);
                return [resolvedTheme, ...mixed];
            }

            return this.pickRandomDistinct(allThemeKeys, 3);
        }

        if (resolvedTheme) {
            return [resolvedTheme];
        }

        return [this.pickRandom(allThemeKeys)].filter(Boolean);
    }

    getRandomPromptFieldValue(fieldName, options = {}) {
        const themeKeys = this.getThemeKeysForOptions(options);

        if (!themeKeys.length) {
            return fieldName === 'exclude'
                ? this.getRandomExcludeValues()
                : this.getRandomPoolValue(fieldName);
        }

        const themePool = [];
        themeKeys.forEach((themeKey) => {
            const theme = this.promptThemes[themeKey];
            if (theme && Array.isArray(theme[fieldName])) {
                themePool.push(...theme[fieldName]);
            }
        });

        if (!themePool.length) {
            return fieldName === 'exclude'
                ? this.getRandomExcludeValues()
                : this.getRandomPoolValue(fieldName);
        }

        const selectedValue = this.pickRandom(themePool);

        if (fieldName === 'exclude') {
            return selectedValue ? [selectedValue] : this.getRandomExcludeValues();
        }

        return selectedValue || this.getRandomPoolValue(fieldName);
    }

    getRandomPromptPreset(options = {}) {
        return {
            scene: this.getRandomPromptFieldValue('scene', options),
            subject: this.getRandomPromptFieldValue('subject', options),
            camera: this.getRandomPromptFieldValue('camera', options),
            lighting: this.getRandomPromptFieldValue('lighting', options),
            audio: this.getRandomPromptFieldValue('audio', options),
            exclude: this.getRandomPromptFieldValue('exclude', options)
        };
    }

    getRandomPoolValue(poolName) {
        const pool = this.promptGeneratorPools[poolName] || [];
        if (!pool.length) {
            return '';
        }

        return pool[Math.floor(Math.random() * pool.length)];
    }

    getRandomExcludeValues() {
        const pool = this.promptGeneratorPools.exclude || [];
        if (pool.length < 2) {
            return pool;
        }

        const firstIndex = Math.floor(Math.random() * pool.length);
        let secondIndex = Math.floor(Math.random() * pool.length);

        while (secondIndex === firstIndex) {
            secondIndex = Math.floor(Math.random() * pool.length);
        }

        return [pool[firstIndex], pool[secondIndex]];
    }

    getGenreFusion(fieldName, themeKeys = [], options = {}) {
        if (themeKeys.length < 2) {
            return this.getRandomPromptFieldValue(fieldName, options);
        }

        const fusionPool = [];
        themeKeys.forEach((themeKey) => {
            const theme = this.promptThemes[themeKey];
            if (theme && Array.isArray(theme[fieldName])) {
                fusionPool.push(...theme[fieldName]);
            }
        });

        if (!fusionPool.length) {
            return this.getRandomPoolValue(fieldName);
        }

        const primaryTheme = this.pickRandom(themeKeys);
        const primaryEntries = this.promptThemes[primaryTheme]?.[fieldName] || [];

        if (primaryEntries.length && Math.random() > 0.3) {
            return this.pickRandom(primaryEntries);
        }

        return this.pickRandom(fusionPool);
    }

    getChaosFusion(fieldName, options = {}) {
        const allThemeKeys = Object.keys(this.promptThemes || {});
        if (!allThemeKeys.length) {
            return this.getRandomPoolValue(fieldName);
        }

        const numThemes = options.chaosDepth || 3;
        const selectedThemes = this.pickRandomDistinct(allThemeKeys, Math.min(numThemes, allThemeKeys.length));

        return this.getGenreFusion(fieldName, selectedThemes, options);
    }

    getHybridPreset(options = {}) {
        const primaryTheme = options.primaryTheme || null;
        const secondaryTheme = options.secondaryTheme || null;

        const themeKeys = [];
        if (primaryTheme) {
            const resolved = this.resolvePromptThemeKey(primaryTheme);
            if (resolved) themeKeys.push(resolved);
        }
        if (secondaryTheme) {
            const resolved = this.resolvePromptThemeKey(secondaryTheme);
            if (resolved && !themeKeys.includes(resolved)) {
                themeKeys.push(resolved);
            }
        }

        if (themeKeys.length === 0) {
            return this.getRandomPromptPreset(options);
        }

        return {
            scene: this.getGenreFusion('scene', themeKeys, options),
            subject: this.getGenreFusion('subject', themeKeys, options),
            camera: this.getGenreFusion('camera', themeKeys, options),
            lighting: this.getGenreFusion('lighting', themeKeys, options),
            audio: this.getGenreFusion('audio', themeKeys, options),
            exclude: this.getGenreFusion('exclude', themeKeys, options)
        };
    }

    getContrastPreset(options = {}) {
        const allThemeKeys = Object.keys(this.promptThemes || {});
        if (allThemeKeys.length < 2) {
            return this.getRandomPromptPreset(options);
        }

        const selected = this.pickRandomDistinct(allThemeKeys, 2);

        return {
            scene: this.getGenreFusion('scene', selected, options),
            subject: this.getGenreFusion('subject', selected, options),
            camera: this.getGenreFusion('camera', selected, options),
            lighting: this.getGenreFusion('lighting', selected, options),
            audio: this.getGenreFusion('audio', selected, options),
            exclude: this.getGenreFusion('exclude', selected, options)
        };
    }

    getGenreBlendOptions() {
        return {
            mode: 'themed',
            primaryTheme: null,
            secondaryTheme: null,
            chaosDepth: 3,
            blendStyle: 'primary'
        };
    }

    selectPrice(pricing, parameters) {
        if (typeof pricing === 'number') {
            return pricing;
        }

        if (!pricing || typeof pricing !== 'object') {
            return null;
        }

        if (parameters.duration && pricing[parameters.duration] !== undefined) {
            const durationPricing = pricing[parameters.duration];

            if (durationPricing && typeof durationPricing === 'object') {
                if (parameters.resolution && durationPricing[parameters.resolution] !== undefined) {
                    return durationPricing[parameters.resolution];
                }
                if (parameters.sound !== undefined && durationPricing.with_audio !== undefined && durationPricing.without_audio !== undefined) {
                    return parameters.sound ? durationPricing.with_audio : durationPricing.without_audio;
                }
                if (parameters.size && durationPricing[parameters.size] !== undefined) {
                    return durationPricing[parameters.size];
                }
                return Object.values(durationPricing)[0];
            }

            return durationPricing;
        }

        if (parameters.n_frames && pricing[parameters.n_frames] !== undefined) {
            const framePricing = pricing[parameters.n_frames];

            if (framePricing && typeof framePricing === 'object') {
                if (parameters.size && framePricing[parameters.size] !== undefined) {
                    return framePricing[parameters.size];
                }
                return Object.values(framePricing)[0];
            }

            return framePricing;
        }

        if (pricing.base !== undefined) {
            return pricing.base;
        }

        const firstKey = Object.keys(pricing)[0];
        if (firstKey && pricing[firstKey] && typeof pricing[firstKey] === 'object') {
            return Object.values(pricing[firstKey])[0];
        }

        return pricing[firstKey];
    }

    async init() {
        try {
            await this.loadModels();
            await this.loadUserCredits();
            this.setupEventListeners();
            this.renderCategories();
            this.populateModelSelect();
            
            // Auto-select first category and model on load
            const categories = this.models.categories || [];
            if (categories.length > 0) {
                const categorySelect = document.getElementById('category-select');
                if (categorySelect) {
                    categorySelect.value = categories[0].id;
                    categorySelect.dispatchEvent(new Event('change'));
                }
            }
        } catch (error) {
            console.error('Failed to initialize VideoGenForms:', error);
            this.showError('Failed to load video generation models');
        }
    }

    async loadUserCredits() {
        const creditsElement = document.getElementById('credits');
        if (!creditsElement) return;

        try {
            const response = await fetch('/api/ai/usercredits');
            const data = await response.json();

            if (data.code === 200 && data.data && typeof data.data.credits !== 'undefined') {
                creditsElement.textContent = data.data.credits;
                return;
            }

            creditsElement.textContent = '-';
        } catch (error) {
            creditsElement.textContent = '-';
        }
    }

    async loadModels() {
        try {
            const response = await fetch('/api/ai/models');
            const data = await response.json();
            if (data.code === 200) {
                this.models = data.data.models;
            } else {
                throw new Error(data.msg || 'Failed to load models');
            }
        } catch (error) {
            throw error;
        }
    }

    setupEventListeners() {
        const categorySelect = document.getElementById('category-select');
        const modelSelect = document.getElementById('model-select');

        if (categorySelect) {
            categorySelect.addEventListener('change', (e) => {
                this.selectCategory(e.target.value);
            });
        }

        if (modelSelect) {
            modelSelect.addEventListener('change', (e) => {
                this.selectModel(e.target.value);
            });
        }

        document.addEventListener('click', (e) => {
            if (e.target.matches('#generate-btn')) {

                this.generateVideo();
            }
        });

        // Form changes for cost calculation
        document.addEventListener('change', (e) => {
            if (e.target.matches('#model-form select, #model-form input')) {
                this.updateCostDisplay();
            }
        });
    }

    renderCategories() {
        const categorySelect = document.getElementById('category-select');
        if (!categorySelect) return;

        const categories = this.models.categories || [];
        categorySelect.innerHTML = categories.map((category) => `
            <option value="${category.id}" ${category.id === this.currentCategory ? 'selected' : ''}>${category.name}</option>
        `).join('');
    }

    getModelsForCategory(categoryId) {
        const providers = this.models.providers || {};
        return Object.entries(providers).flatMap(([providerId, provider]) => {
            return Object.entries(provider.models || {})
                .filter(([_, model]) => model.category === categoryId)
                .map(([modelId, model]) => ({
                    providerId,
                    providerName: provider.name,
                    modelId,
                    model
                }));
        });
    }

    populateModelSelect() {
        const modelSelect = document.getElementById('model-select');
        if (!modelSelect) return;

        this.availableModels = this.currentCategory ? this.getModelsForCategory(this.currentCategory) : [];
        modelSelect.disabled = this.availableModels.length === 0;
        
        // Auto-select first model if none selected
        if (!this.currentModel && this.availableModels.length > 0) {
            this.currentModel = this.availableModels[0].modelId;
        }
        
        modelSelect.innerHTML = this.availableModels.map(({ modelId, model, providerName }) => `
            <option value="${modelId}" ${modelId === this.currentModel ? 'selected' : ''}>
                ${providerName} - ${model.name} (${this.getLowestPrice(model.pricing)} credits)
            </option>
        `).join('');
    }

    selectCategory(categoryId) {
        this.currentCategory = categoryId;
        this.currentModel = null;
        this.populateModelSelect();
        
        // Auto-select first model in category
        const firstModel = this.availableModels[0];
        if (firstModel) {
            this.selectModel(firstModel.modelId);
        } else {
            this.hideModelForm();
        }
    }

    selectModel(modelId) {
        this.currentModel = modelId;

        if (!modelId) {
            this.hideModelForm();
            return;
        }

        this.renderModelForm(modelId);
    }

    renderModelForm(modelId) {
        const formContainer = document.getElementById('model-form-container');
        const formContent = document.getElementById('model-form');
        if (!formContainer || !formContent) return;

        // Find the model configuration
        let modelConfig = null;
        for (const provider of Object.values(this.models.providers || {})) {
            if (provider.models[modelId]) {
                modelConfig = provider.models[modelId];
                break;
            }
        }

        if (!modelConfig) return;

        const formFields = Object.entries(modelConfig.form || {}).map(([fieldId, fieldConfig]) => {
            return this.renderFormField(fieldId, fieldConfig);
        }).join('');

        formContent.innerHTML = `
            <h3>Generate with ${modelConfig.name}</h3>
            <form id="video-generation-form">
                ${formFields}
                <div id="cost-display" class="cost-display">
                    <h4>Call Cost</h4>
                    <div id="cost-details"></div>
                </div>
                <div class="form-actions">
                    <button type="button" id="generate-btn" class="primary-btn">
                        Generate Video
                    </button>
                </div>
            </form>
        `;

        formContainer.style.display = 'block';
        this.updateCostDisplay();
        this.initializeFilePreviews();
        this.renderPromptGeneratorPanel();
        this.initializePromptGenerator();
    }

    renderPromptGeneratorPanel() {
        const formContainer = document.getElementById('model-form-container');
        if (!formContainer) return;

        const title = this.currentModel && this.currentModel.includes('sora')
            ? 'Sora Prompt Generator'
            : 'Prompt Generator';
        const themeOptions = this.getPromptThemeOptionsHtml();

        // Check if panel already exists and remove it
        const existingPanel = document.getElementById('prompt-generator-panel');
        if (existingPanel) {
            existingPanel.remove();
        }

        // Create generator panel as a div inside the form container
        const panel = document.createElement('div');
        panel.id = 'prompt-generator-panel';
        panel.innerHTML = `
            <div class="prompt-builder-card">
                <h2 id="generator-title" class="section">${title}</h2>
              
                 
                <div class="prompt-generator-controls">
                    <label class="control-field">
                        <span>Thematic Filter</span>
                        <select class="prompt-theme-select" aria-label="Select prompt theme">
                            ${themeOptions}
                        </select>
                    </label>
                    <label class="control-field">
                        <span>Random Mode</span>
                        <select class="prompt-random-mode-select" aria-label="Select random mode">
                            <option value="themed">Thematic Focus</option>
                            <option value="chaos">Chaos Mix</option>
                        </select>
                    </label>
                </div>
                <div class="prompt-builder-form" autocomplete="off" aria-label="Prompt Generator Form">
                    <label class="field">
                        <span>Scene</span>
                        <div class="field-row">
                            <input data-prompt-builder="scene" type="text" placeholder="futuristic city skyline at sunset" aria-label="Enter scene" title="Scene input">
                            <button class="field-select-btn" data-select-for="scene" type="button" title="Select from list">...</button>
                            <button class="field-rdm-btn" data-rdm-for="scene" type="button">RDM</button>
                            <button class="field-add-btn" data-add-for="scene" type="button" title="Add custom entry">+</button>
                        </div>
                    </label>
                    <label class="field">
                        <span>Subject</span>
                        <div class="field-row">
                            <input data-prompt-builder="subject" type="text" placeholder="courier adjusts helmet, breath visible" aria-label="Enter subject" title="Subject input">
                            <button class="field-select-btn" data-select-for="subject" type="button" title="Select from list">...</button>
                            <button class="field-rdm-btn" data-rdm-for="subject" type="button">RDM</button>
                            <button class="field-add-btn" data-add-for="subject" type="button" title="Add custom entry">+</button>
                        </div>
                    </label>
                    <label class="field">
                        <span>Camera or motion</span>
                        <div class="field-row">
                            <input data-prompt-builder="camera" type="text" placeholder="tracking shot, slow pan, dolly-in" aria-label="Enter camera or motion" title="Camera or motion input">
                            <button class="field-select-btn" data-select-for="camera" type="button" title="Select from list">...</button>
                            <button class="field-rdm-btn" data-rdm-for="camera" type="button">RDM</button>
                            <button class="field-add-btn" data-add-for="camera" type="button" title="Add custom entry">+</button>
                        </div>
                    </label>
                    <label class="field">
                        <span>Lighting and mood</span>
                        <div class="field-row">
                            <input data-prompt-builder="lighting" type="text" placeholder="golden hour, moody, vibrant" aria-label="Enter lighting and mood" title="Lighting and mood input">
                            <button class="field-select-btn" data-select-for="lighting" type="button" title="Select from list">...</button>
                            <button class="field-rdm-btn" data-rdm-for="lighting" type="button">RDM</button>
                            <button class="field-add-btn" data-add-for="lighting" type="button" title="Add custom entry">+</button>
                        </div>
                    </label>
                    <label class="field">
                        <span>Audio</span>
                        <div class="field-row">
                            <input data-prompt-builder="audio" type="text" placeholder="ambient city hum" aria-label="Enter optional audio" title="Audio input">
                            <button class="field-select-btn" data-select-for="audio" type="button" title="Select from list">...</button>
                            <button class="field-rdm-btn" data-rdm-for="audio" type="button">RDM</button>
                            <button class="field-add-btn" data-add-for="audio" type="button" title="Add custom entry">+</button>
                        </div>
                    </label>
                    <label class="field full">
                        <span>Exclude</span>
                        <div class="field-row">
                            <input data-prompt-builder="exclude" type="text" placeholder="text overlays, Dutch angles" aria-label="Enter exclusions" title="Exclusions input">
                            <button class="field-select-btn" data-select-for="exclude" type="button" title="Select from list">...</button>
                            <button class="field-rdm-btn" data-rdm-for="exclude" type="button">RDM</button>
                            <button class="field-add-btn" data-add-for="exclude" type="button" title="Add custom entry">+</button>
                        </div>
                    </label>
                    <div class="full prompt-builder-actions">
                        <button class="btn prompt-random-btn" type="button">Randomize</button>
                        <button class="btn prompt-copy-btn" type="button">Copy</button>
                        <button class="btn prompt-apply-btn" type="button">Use in Prompt</button>
                    </div>
                </div>
                <pre class="output prompt-builder-output" aria-live="polite" aria-label="Generated prompt output">Build a prompt from the fields above.</pre>
            </div>
            <div id="field-selector-modal" class="vg-modal hidden">
                <div class="vg-modal-content">
                    <div class="vg-modal-header">
                        <h3 id="selector-modal-title">Select Entry</h3>
                        <button class="vg-modal-close" type="button">&times;</button>
                    </div>
                    <div class="vg-modal-body">
                        <input type="text" id="selector-search" class="vg-modal-search" placeholder="Search...">
                        <div id="selector-list" class="vg-modal-list"></div>
                    </div>
                </div>
            </div>
            <div id="field-add-modal" class="vg-modal hidden">
                <div class="vg-modal-content">
                    <div class="vg-modal-header">
                        <h3 id="add-modal-title">Add Entries</h3>
                        <button class="vg-modal-close" type="button">&times;</button>
                    </div>
                    <div class="vg-modal-body">
                        <p id="add-modal-field" class="vg-modal-field-label"></p>
                        <input type="text" id="add-search" class="vg-modal-search" placeholder="Search...">
                        <div id="add-list" class="vg-modal-list vg-modal-list-multi"></div>
                        <p class="vg-modal-hint">Click entries to select/deselect</p>
                    </div>
                    <div class="vg-modal-footer">
                        <span id="add-selected-count" class="vg-modal-count">0 selected</span>
                        <button class="btn vg-modal-cancel" type="button">Cancel</button>
                        <button class="btn primary vg-modal-confirm" type="button">Add Selected</button>
                    </div>
                </div>
            </div>
        `;

        // Append panel to form container
        formContainer.appendChild(panel);

        // Add toast container
        let toastContainer = document.getElementById('vg-toast-container');
        if (!toastContainer) {
            toastContainer = document.createElement('div');
            toastContainer.id = 'vg-toast-container';
            toastContainer.className = 'vg-toast-container';
            document.body.appendChild(toastContainer);
        }
    }

    renderPromptGenerator(fieldId, fieldConfig) {
        // Return empty string since we render the generator separately now
        return '';
    }

    renderFormField(fieldId, fieldConfig) {
        const { type, label, required, placeholder, options, default: defaultValue, ...otherProps } = fieldConfig;

        let fieldHtml = '';

        switch (type) {
            case 'textarea':
                if (fieldId === 'prompt') {
                    fieldHtml = `
                    <div class="form-group">
                        <label for="${fieldId}">${label}${required ? ' *' : ''}</label>
                        <textarea id="${fieldId}" name="${fieldId}" 
                                  placeholder="${placeholder || ''}" 
                                  ${required ? 'required' : ''}
                                  rows="${otherProps.rows || 4}"></textarea>
                    </div>
                `;
                    break;
                }

                fieldHtml = `
                    <div class="form-group">
                        <label for="${fieldId}">${label}${required ? ' *' : ''}</label>
                        <textarea id="${fieldId}" name="${fieldId}" 
                                  placeholder="${placeholder || ''}" 
                                  ${required ? 'required' : ''}
                                  rows="${otherProps.rows || 4}"></textarea>
                    </div>
                `;
                break;

            case 'select':
                let effectiveDefault = defaultValue;
                if (options.includes('16:9')) {
                    effectiveDefault = '16:9';
                }
                fieldHtml = `
                    <div class="form-group">
                        <label for="${fieldId}">${label}${required ? ' *' : ''}</label>
                        <select id="${fieldId}" name="${fieldId}" ${required ? 'required' : ''}>
                            ${options.map(option =>
                    `<option value="${option}" ${option === effectiveDefault ? 'selected' : ''}>${option}</option>`
                ).join('')}
                        </select>
                    </div>
                `;
                break;

            case 'number':
                fieldHtml = `
                    <div class="form-group">
                        <label for="${fieldId}">${label}${required ? ' *' : ''}</label>
                        <div class="number-input-wrapper">
                            <input type="number" id="${fieldId}" name="${fieldId}" 
                                   ${required ? 'required' : ''}
                                   min="${otherProps.min || ''}"
                                   max="${otherProps.max || ''}"
                                   step="${otherProps.step || ''}"
                                   value="${defaultValue || ''}">
                        </div>
                    </div>
                `;
                break;

            case 'checkbox':
                fieldHtml = `
                    <div class="form-group">
                        <label class="checkbox-label">
                            <input type="checkbox" id="${fieldId}" name="${fieldId}" 
                                   ${defaultValue ? 'checked' : ''}>
                            ${label}
                        </label>
                    </div>
                `;
                break;

            case 'file':
                fieldHtml = `
                    <div class="form-group file-input-group">
                        <label for="${fieldId}">${label}${required ? ' *' : ''}</label>
                        <input type="file" id="${fieldId}" name="${fieldId}" 
                               class="file-input-control"
                               ${required ? 'required' : ''}
                               accept="${otherProps.accept || ''}"
                               ${otherProps.multiple ? 'multiple' : ''}>
                        <div class="file-preview" id="${fieldId}-preview" data-preview-for="${fieldId}">No file selected</div>
                    </div>
                `;
                break;

            case 'text':
            default:
                fieldHtml = `
                    <div class="form-group">
                        <label for="${fieldId}">${label}${required ? ' *' : ''}</label>
                        <input type="text" id="${fieldId}" name="${fieldId}" 
                               placeholder="${placeholder || ''}" 
                               ${required ? 'required' : ''}
                               value="${defaultValue || ''}">
                    </div>
                `;
                break;
        }

        return fieldHtml;
    }

    initializeFilePreviews() {
        const form = document.getElementById('video-generation-form');
        if (!form) {
            return;
        }

        const fileInputs = form.querySelectorAll('input[type="file"]');
        fileInputs.forEach((input) => {
            const preview = document.getElementById(`${input.id}-preview`);
            if (!preview) {
                return;
            }

            let isUploading = false;

            const uploadAndRender = async () => {
                const [file] = input.files || [];

                if (!file || isUploading) {
                    return;
                }

                isUploading = true;

                const safeName = this.escapeHtml(file.name);
                const fileName = String(file.name || '').toLowerCase();
                const accept = String(input.accept || '').toLowerCase();
                const isImageFile = (
                    (file.type && file.type.startsWith('image/'))
                    || accept.includes('image/')
                    || /\.(png|jpe?g|gif|webp|bmp|svg|avif|ico|tiff?)$/i.test(fileName)
                );
                const isVideoFile = (
                    (file.type && file.type.startsWith('video/'))
                    || accept.includes('video/')
                    || /\.(mp4|webm|mov|mkv|avi)$/i.test(fileName)
                );

                preview.innerHTML = `<div class="file-preview-meta">${safeName}<br><em>Uploading...</em></div>`;
                preview.classList.add('has-file');
                preview.classList.remove('has-image-preview');

                try {
                    const urls = await this.uploadFilesForModel([file], this.currentModel);
                    if (urls && urls.length > 0) {
                        this.uploadedFileUrls[input.name] = urls[0];
                        const objectUrl = URL.createObjectURL(file);
                        
                        if (isVideoFile) {
                            preview.innerHTML = `
                                <div class="file-preview-inner">
                                    <video src="${objectUrl}" class="file-preview-video" id="${input.id}-video" muted loop onerror="this.style.display='none'"></video>
                                    <div class="video-preview-controls">
                                        <button type="button" class="video-ctrl-btn play-btn" data-target="${input.id}-video">▶</button>
                                        <button type="button" class="video-ctrl-btn stop-btn" data-target="${input.id}-video">⏹</button>
                                    </div>
                                    <div class="file-preview-meta">${safeName}<br><em style="color:green">Uploaded</em></div>
                                </div>
                            `;
                            preview.classList.add('has-video-preview');
                            
                            preview.querySelectorAll('.video-ctrl-btn').forEach(btn => {
                                btn.addEventListener('click', (e) => {
                                    e.stopPropagation();
                                    const videoEl = document.getElementById(btn.dataset.target);
                                    if (!videoEl) return;
                                    if (btn.classList.contains('play-btn')) {
                                        if (videoEl.paused) {
                                            videoEl.play();
                                            btn.textContent = '⏸';
                                        } else {
                                            videoEl.pause();
                                            btn.textContent = '▶';
                                        }
                                    } else if (btn.classList.contains('stop-btn')) {
                                        videoEl.pause();
                                        videoEl.currentTime = 0;
                                        preview.querySelector('.play-btn').textContent = '▶';
                                    }
                                });
                            });
                        } else if (isImageFile) {
                            preview.innerHTML = `
                                <div class="file-preview-inner">
                                    <img src="${objectUrl}" alt="${safeName}" class="file-preview-image">
                                    <div class="file-preview-meta">${safeName}<br><em style="color:green">Uploaded</em></div>
                                </div>
                            `;
                            preview.classList.add('has-image-preview');
                        } else {
                            preview.innerHTML = `
                                <div class="file-preview-inner">
                                    <div class="file-preview-meta">${safeName}<br><em style="color:green">Uploaded</em></div>
                                </div>
                            `;
                        }
                    }
                } catch (error) {
                    console.error('Upload failed:', error);
                    preview.innerHTML = `<div class="file-preview-meta">${safeName}<br><em style="color:red">Upload failed: ${error.message}</em></div>`;
                    delete this.uploadedFileUrls[input.name];
                } finally {
                    isUploading = false;
                }
            };

            input.addEventListener('change', uploadAndRender);
        });
    }

    escapeHtml(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    getThemeFieldOptions(fieldName, options = {}) {
        const themeKeys = this.getThemeKeysForOptions(options);
        const fieldEntries = new Map();

        if (themeKeys.length === 0) {
            return [];
        }

        themeKeys.forEach((themeKey) => {
            const theme = this.promptThemes[themeKey];
            if (theme && Array.isArray(theme[fieldName])) {
                theme[fieldName].forEach((entry) => {
                    if (!fieldEntries.has(entry)) {
                        fieldEntries.set(entry, themeKey);
                    }
                });
            }
        });

        return Array.from(fieldEntries.entries()).map(([entry, theme]) => ({
            value: entry,
            theme: theme,
            label: entry
        }));
    }

    updateFieldSelects(builder, options = {}) {
        // No longer using selects - modals are used instead
        // This method is kept for compatibility but does nothing
    }

    initializePromptGenerator() {
        const builder = document.querySelector('#prompt-generator-panel .prompt-builder-card');
        const panel = document.querySelector('#prompt-generator-panel');
        const promptField = document.getElementById('prompt');

        if (!builder || !promptField) {
            return;
        }

        const randomButton = builder.querySelector('.prompt-random-btn');
        const copyButton = builder.querySelector('.prompt-copy-btn');
        const applyButton = builder.querySelector('.prompt-apply-btn');
        const themeSelect = builder.querySelector('.prompt-theme-select');
        const modeSelect = builder.querySelector('.prompt-random-mode-select');
        const output = builder.querySelector('.prompt-builder-output');
        const selectorModal = panel.querySelector('#field-selector-modal');
        const addModal = panel.querySelector('#field-add-modal');

        let currentField = null;

        const updateOutput = () => {
            output.textContent = this.composePromptFromBuilder(builder);
        };

        const applyGeneratedPrompt = () => {
            const generatedPrompt = this.composePromptFromBuilder(builder);
            output.textContent = generatedPrompt;
            promptField.value = generatedPrompt;
            promptField.dispatchEvent(new Event('input', { bubbles: true }));
            promptField.dispatchEvent(new Event('change', { bubbles: true }));
        };

        const openSelectorModal = (fieldName) => {
            currentField = fieldName;
            const options = this.getPromptGeneratorOptions(builder);
            const entries = this.getThemeFieldOptions(fieldName, options);
            const titleEl = selectorModal.querySelector('#selector-modal-title');
            const listEl = selectorModal.querySelector('#selector-list');
            const searchEl = selectorModal.querySelector('#selector-search');

            titleEl.textContent = `Select ${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`;
            listEl.innerHTML = '';

            const renderList = (items) => {
                listEl.innerHTML = '';
                if (items.length === 0) {
                    listEl.innerHTML = '<div class="vg-modal-empty">No entries found</div>';
                    return;
                }
                items.forEach((entry) => {
                    const item = document.createElement('div');
                    item.className = 'vg-modal-item';
                    item.dataset.value = entry.value;
                    item.dataset.theme = entry.theme;
                    item.innerHTML = `<span class="vg-modal-item-text">${entry.label}</span><span class="vg-modal-item-theme">${entry.theme}</span>`;
                    item.addEventListener('click', () => {
                        const input = builder.querySelector(`[data-prompt-builder="${fieldName}"]`);
                        if (input) {
                            input.value = entry.value;
                            updateOutput();
                        }
                        selectorModal.classList.add('hidden');
                    });
                    listEl.appendChild(item);
                });
            };

            renderList(entries);

            searchEl.addEventListener('input', () => {
                const query = searchEl.value.toLowerCase();
                const filtered = entries.filter((e) =>
                    e.label.toLowerCase().includes(query) ||
                    e.theme.toLowerCase().includes(query)
                );
                renderList(filtered);
            });

            selectorModal.classList.remove('hidden');
        };

        const openAddModal = (fieldName) => {
            currentField = fieldName;
            const options = this.getPromptGeneratorOptions(builder);
            const allEntries = this.getThemeFieldOptions(fieldName, options);
            const titleEl = addModal.querySelector('#add-modal-title');
            const fieldLabel = addModal.querySelector('#add-modal-field');
            const listEl = addModal.querySelector('#add-list');
            const searchEl = addModal.querySelector('#add-search');
            const countEl = addModal.querySelector('#add-selected-count');
            const selectedEntries = new Set();

            titleEl.textContent = `Add ${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`;
            fieldLabel.textContent = `Adding to: ${fieldName}`;
            listEl.innerHTML = '';

            const updateCount = () => {
                countEl.textContent = `${selectedEntries.size} selected`;
            };

            const renderList = (items) => {
                listEl.innerHTML = '';
                if (items.length === 0) {
                    listEl.innerHTML = '<div class="vg-modal-empty">No entries found</div>';
                    return;
                }
                items.forEach((entry) => {
                    const item = document.createElement('div');
                    item.className = 'vg-modal-item vg-modal-item-selectable';
                    item.dataset.value = entry.value;
                    item.dataset.theme = entry.theme;
                    item.innerHTML = `<span class="vg-modal-item-text">${entry.label}</span><span class="vg-modal-item-theme">${entry.theme}</span><span class="vg-modal-item-check"></span>`;

                    if (selectedEntries.has(entry.value)) {
                        item.classList.add('selected');
                    }

                    item.addEventListener('click', () => {
                        if (selectedEntries.has(entry.value)) {
                            selectedEntries.delete(entry.value);
                            item.classList.remove('selected');
                        } else {
                            selectedEntries.add(entry.value);
                            item.classList.add('selected');
                        }
                        updateCount();
                    });
                    listEl.appendChild(item);
                });
            };

            renderList(allEntries);
            updateCount();

            searchEl.addEventListener('input', () => {
                const query = searchEl.value.toLowerCase();
                const filtered = allEntries.filter((e) =>
                    e.label.toLowerCase().includes(query) ||
                    e.theme.toLowerCase().includes(query)
                );
                renderList(filtered);
            });

            const confirmAdd = () => {
                if (selectedEntries.size > 0 && currentField) {
                    const input = builder.querySelector(`[data-prompt-builder="${currentField}"]`);
                    if (input) {
                        const current = input.value.trim();
                        const selectedArray = Array.from(selectedEntries);
                        const newValue = current ? `${current}, ${selectedArray.join(', ')}` : selectedArray.join(', ');
                        input.value = newValue;
                        updateOutput();
                    }
                }
                closeAddModal();
            };

            const closeAddModalHandler = () => {
                searchEl.removeEventListener('input', () => { });
                addModal.classList.add('hidden');
            };

            addModal.querySelector('.vg-modal-close').removeEventListener('click', closeAddModalHandler);
            addModal.querySelector('.vg-modal-cancel').removeEventListener('click', closeAddModalHandler);
            addModal.querySelector('.vg-modal-confirm').removeEventListener('click', confirmAdd);

            addModal.querySelector('.vg-modal-close').addEventListener('click', closeAddModalHandler);
            addModal.querySelector('.vg-modal-cancel').addEventListener('click', closeAddModalHandler);
            addModal.querySelector('.vg-modal-confirm').addEventListener('click', confirmAdd);

            addModal.classList.remove('hidden');
        };

        const closeSelectorModal = () => {
            selectorModal.classList.add('hidden');
            selectorModal.querySelector('#selector-search').value = '';
        };

        const closeAddModal = () => {
            addModal.classList.add('hidden');
            addModal.querySelector('#add-search').value = '';
        };

        selectorModal.querySelector('.vg-modal-close').addEventListener('click', closeSelectorModal);
        selectorModal.querySelector('.vg-modal-cancel')?.addEventListener('click', closeSelectorModal);
        addModal.querySelector('.vg-modal-close').addEventListener('click', closeAddModal);
        addModal.querySelector('.vg-modal-cancel').addEventListener('click', closeAddModal);

        selectorModal.addEventListener('click', (e) => {
            if (e.target === selectorModal) {
                closeSelectorModal();
            }
        });

        addModal.addEventListener('click', (e) => {
            if (e.target === addModal) {
                closeAddModal();
            }
        });

        builder.querySelectorAll('[data-prompt-builder]').forEach((input) => {
            input.addEventListener('input', updateOutput);
        });

        builder.querySelectorAll('.field-select-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const field = btn.dataset.selectFor;
                openSelectorModal(field);
            });
        });

        builder.querySelectorAll('.field-add-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const field = btn.dataset.addFor;
                openAddModal(field);
            });
        });

        // Single-field randomize buttons
        builder.querySelectorAll('.field-rdm-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const field = btn.dataset.rdmFor;
                const options = this.getPromptGeneratorOptions(builder);
                const value = this.getRandomPromptFieldValue(field, options) || '';
                const input = builder.querySelector(`[data-prompt-builder="${field}"]`);
                if (input) {
                    input.value = Array.isArray(value) ? value.join(', ') : value;
                    updateOutput();
                }
            });
        });

        const updateSelects = () => {
            const options = this.getPromptGeneratorOptions(builder);
            this.updateFieldSelects(builder, options);
        };

        const randomizeWithSelection = () => {
            const options = this.getPromptGeneratorOptions(builder);
            const preset = this.getRandomPromptPreset(options);
            this.populatePromptBuilder(builder, preset);
            updateSelects();
            updateOutput();
        };

        randomButton.addEventListener('click', () => {
            randomizeWithSelection();
        });

        if (themeSelect) {
            themeSelect.addEventListener('change', () => {
                updateSelects();
                randomizeWithSelection();
            });
        }

        if (modeSelect) {
            modeSelect.addEventListener('change', randomizeWithSelection);
        }

        copyButton.addEventListener('click', async () => {
            const generatedPrompt = this.composePromptFromBuilder(builder);
            try {
                await navigator.clipboard.writeText(generatedPrompt);
            } catch (error) {
                output.textContent = generatedPrompt;
            }
        });

        applyButton.addEventListener('click', applyGeneratedPrompt);

        const defaultPreset = this.getRandomPromptPreset(this.getPromptGeneratorOptions(builder));
        this.populatePromptBuilder(builder, defaultPreset);
        updateOutput();
    }

    populatePromptBuilder(builder, preset) {
        const values = {
            scene: preset.scene || '',
            subject: preset.subject || '',
            camera: preset.camera || '',
            lighting: preset.lighting || '',
            audio: preset.audio || '',
            exclude: Array.isArray(preset.exclude) ? preset.exclude.join(', ') : (preset.exclude || '')
        };

        Object.entries(values).forEach(([key, value]) => {
            const input = builder.querySelector(`[data-prompt-builder="${key}"]`);
            if (input) {
                input.value = value;
            }
        });
    }

    composePromptFromBuilder(builder) {
        const getValue = (key) => {
            const input = builder.querySelector(`[data-prompt-builder="${key}"]`);
            return input ? input.value.trim() : '';
        };

        const scene = getValue('scene');
        const subject = getValue('subject');
        const camera = getValue('camera');
        const lighting = getValue('lighting');
        const audio = getValue('audio');
        const exclude = getValue('exclude')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);

        const parts = [
            `Text-to-video prompt:`,
            scene ? `Scene: ${scene}.` : '',
            subject ? `Subject: ${subject}.` : '',
            camera ? `Camera/Motion: ${camera}.` : '',
            lighting ? `Lighting & Mood: ${lighting}.` : '',
            `Composition: clear subject, layered depth, natural lens behavior, subtle motion blur.`,
            audio ? `Audio (optional): ${audio}.` : '',
            exclude.length ? `Exclusions: ${exclude.join('; ')}.` : ''
        ].filter(Boolean);

        return parts.join(' ');
    }

    hideModelForm() {
        const formContainer = document.getElementById('model-form-container');
        if (formContainer) {
            formContainer.style.display = 'none';
        }
    }

    getLowestPrice(pricing) {
        if (typeof pricing === 'number') {
            return pricing;
        }

        if (typeof pricing === 'object') {
            let lowest = Infinity;
            for (const value of Object.values(pricing)) {
                if (typeof value === 'number') {
                    lowest = Math.min(lowest, value);
                } else if (typeof value === 'object') {
                    for (const subValue of Object.values(value)) {
                        if (typeof subValue === 'number') {
                            lowest = Math.min(lowest, subValue);
                        }
                    }
                }
            }
            return lowest === Infinity ? 'N/A' : lowest;
        }

        return 'N/A';
    }

    calculateCost() {
        if (!this.currentModel) return null;

        // Get form values
        const form = document.getElementById('video-generation-form');
        if (!form) return null;

        const formData = new FormData(form);
        const parameters = {};

        for (const [key, value] of formData.entries()) {
            if (value) {
                parameters[key] = value;
            }
        }

        // Handle checkboxes
        form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
            parameters[checkbox.name] = checkbox.checked;
        });

        // Find model pricing
        let modelConfig = null;
        for (const provider of Object.values(this.models.providers || {})) {
            if (provider.models[this.currentModel]) {
                modelConfig = provider.models[this.currentModel];
                break;
            }
        }

        if (!modelConfig) return null;

        const ggvCost = this.selectPrice(modelConfig.pricing, parameters);

        return {
            ggv: ggvCost
        };
    }

    updateCostDisplay() {
        const cost = this.calculateCost();
        const costDisplay = document.getElementById('cost-display');
        const costDetails = document.getElementById('cost-details');

        if (cost && costDisplay && costDetails) {
            costDetails.innerHTML = `
                <div class="cost-item">
                    <span>Price:</span>
                    <span class="ggv-cost">${cost.ggv}</span>
                </div>
                <div class="conversion-note">
                    credits per model call
                </div>
            `;
        } else if (costDetails) {
            costDetails.innerHTML = `
                <div class="cost-item">
                    <span>Price:</span>
                    <span class="ggv-cost">-</span>
                </div>
            `;
        }
    }

    async uploadFilesForModel(files, model) {
        const formData = new FormData();
        for (const file of files) {
            formData.append('files', file);
        }
        formData.append('model', model);

        try {
            const response = await fetch('/api/ai/upload', {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            if (data.code === 200 && data.data && data.data.urls) {
                return data.data.urls;
            } else {
                throw new Error(data.msg || 'File upload failed');
            }
        } catch (error) {
            console.error('File upload error:', error);
            throw error;
        }
    }

    async generateVideo() {
        
        if (!this.currentModel) {
            this.showError('Please select a model first');
            return;
        }

        if (this.isGenerating) {
            this.showError('Generation already in progress...');
            return;
        }

        const form = document.getElementById('video-generation-form');
        if (!form) return;

        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        const formData = new FormData(form);
        const parameters = {};

        for (const [key, value] of formData.entries()) {
            if (value) {
                parameters[key] = value;
            }
        }

        form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
            parameters[checkbox.name] = checkbox.checked;
        });

        const fileInputs = form.querySelectorAll('input[type="file"]');
        const fileFieldMap = {
            'image_url': 'image_urls',
            'image_urls': 'image_urls',
            'video_url': 'video_urls',
            'video_urls': 'video_urls',
            'input_urls': 'input_urls'
        };

        for (const fileInput of fileInputs) {
            if (fileInput.files.length > 0) {
                const uploadedUrl = this.uploadedFileUrls[fileInput.name];
                if (!uploadedUrl) {
                    this.showError('Please wait for file upload to complete before generating');
                    return;
                }
                const kieFieldName = fileFieldMap[fileInput.name] || fileInput.name;
                parameters[fileInput.name] = uploadedUrl;
                parameters[kieFieldName] = [uploadedUrl];
            }
        }

        parameters.model = this.currentModel;

        try {
            this.isGenerating = true;
            const generateBtn = document.getElementById('generate-btn');
            generateBtn.disabled = true;
            generateBtn.textContent = 'Generating...';
            this.updateStatus('Starting generation...');

            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(parameters)
            });

            const data = await response.json();

            if (data.code === 200) {
                await this.loadUserCredits();
                this.showSuccess(`Video generation started! Task ID: ${data.data.taskId}`);
                await this.pollForResult(data.data.taskId);
            } else {
                this.showError(data.msg || 'Generation failed');
            }
        } catch (error) {
            console.error('Generation error:', error);
            this.showError('Network error occurred during generation');
        } finally {
            this.isGenerating = false;
            const generateBtn = document.getElementById('generate-btn');
            if (generateBtn) {
                generateBtn.disabled = false;
                generateBtn.textContent = 'Generate Video';
            }
        }
    }

    updateStatus(message) {
        const placeholder = document.getElementById('placeholder');
        if (placeholder) {
            const statusEl = placeholder.querySelector('.generating-status');
            if (statusEl) {
                statusEl.textContent = message;
            }
        }
    }

    async pollForResult(taskId) {
        const maxAttempts = 120; // Maximum 10 minutes (120 * 5 seconds)
        let attempts = 0;
        
        const generateBtn = document.getElementById('generate-btn');
        const videoElement = document.getElementById('outputvideo');
        const placeholder = document.getElementById('placeholder');
        const videoControls = document.getElementById('videoControls');
        
        // Hide video and show placeholder with animation
        if (videoElement) {
            videoElement.style.display = 'none';
            videoElement.classList.remove('visible');
        }
        if (videoControls) {
            videoControls.classList.remove('visible');
        }
        if (placeholder) {
            placeholder.style.display = 'flex';
            placeholder.innerHTML = `<p class="generating-status">Generating |</p>`;
        }
        
        // Animation frames for the spinner - runs independently of polling
        const animationFrames = ['|', '/', '-', '\\'];
        let frameIndex = 0;
        const animationInterval = setInterval(() => {
            const statusText = placeholder?.querySelector('.generating-status');
            if (statusText) {
                statusText.textContent = `Generating ${animationFrames[frameIndex]}`;
                frameIndex = (frameIndex + 1) % animationFrames.length;
            }
        }, 150);
        
        while (attempts < maxAttempts && this.isGenerating) {
            try {
                const response = await fetch(`/api/ai/status`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ taskId })
                });
                const data = await response.json();
                
                const taskData = data.data || {};
                
                if (taskData.status === 'completed' && taskData.result) {
                    clearInterval(animationInterval);
                    this.showSuccess('Video generation completed! Preparing video...');
                    
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    
                    if (typeof window.listVideos === 'function') {
                        await window.listVideos();
                    }
                    
                    if (typeof window.loadVideo === 'function') {
                        await window.loadVideo(taskData.result.videoBucketKey);
                    }
                    
                    if (placeholder) placeholder.style.display = 'none';
                    break;
                } else if (taskData.status === 'failed') {
                    clearInterval(animationInterval);
                    const errMsg = taskData.errorMessage || 'Video generation failed';
                    this.showError(errMsg);
                    
                    if (placeholder) {
                        placeholder.innerHTML = `
                            <div class="generation-failed">
                                <h3>Generation Failed</h3>
                                <p>${errMsg}</p>
                                <button class="retry-btn" onclick="location.reload()">Try Again</button>
                            </div>
                        `;
                    }
                    
                    break;
                } else {
                    attempts++;
                    generateBtn.textContent = 'Generating...';
                    await new Promise(resolve => setTimeout(resolve, 5000));
                }
            } catch (error) {
                console.error('Polling error:', error);
                attempts++;
                await new Promise(resolve => setTimeout(resolve, 5000));
            }
        }
        
        if (attempts >= maxAttempts) {
            clearInterval(animationInterval);
            this.showError('Generation timeout - please check back later');
            
            if (placeholder) {
                placeholder.innerHTML = `<p class="generating-status">Timeout — check back later</p>`;
            }
        }
    }

    showError(message, title = 'Error') {
        this.showToast(message, title, 'error');
    }

    showSuccess(message, title = 'Success') {
        this.showToast(message, title, 'success');
    }

    showToast(message, title = '', type = 'info') {
        let toastContainer = document.getElementById('vg-toast-container');
        if (!toastContainer) {
            toastContainer = document.createElement('div');
            toastContainer.id = 'vg-toast-container';
            toastContainer.className = 'vg-toast-container';
            document.body.appendChild(toastContainer);
        }

        const toast = document.createElement('div');
        toast.className = `vg-toast vg-toast-${type}`;
        toast.innerHTML = `
            <div class="vg-toast-header">
                <span class="vg-toast-title">${title}</span>
                <button class="vg-toast-close" type="button">&times;</button>
            </div>
            <div class="vg-toast-body">${message}</div>
        `;

        const closeBtn = toast.querySelector('.vg-toast-close');
        closeBtn.addEventListener('click', () => {
            toast.classList.remove('vg-toast-show');
            setTimeout(() => toast.remove(), 300);
        });

        toastContainer.appendChild(toast);

        // Trigger animation
        setTimeout(() => {
            toast.classList.add('vg-toast-show');
        }, 10);

        // Auto remove after 5 seconds
        setTimeout(() => {
            if (toast.parentNode) {
                toast.classList.remove('vg-toast-show');
                setTimeout(() => toast.remove(), 300);
            }
        }, 5000);
    }
}


// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new VideoGenForms();
    
    let dashboardPlayer = null;

    window.loadVideo = async function loadVideo(key) {
        const placeholder = document.getElementById('placeholder');
        const videoElement = document.getElementById('outputvideo');
        const controlsContainer = document.getElementById('videoControls');
        
        // hide placeholder
        placeholder.style.display = 'none';
        // show video
        videoElement.style.display = 'block';
        videoElement.classList.add('visible');
        // show controls
        if (controlsContainer) {
            controlsContainer.classList.add('visible');
        }
        // set src
        videoElement.src = `/dashboard/media/serve/${key}`;
        
        // Add click-to-play/pause functionality
        videoElement.addEventListener('click', function() {
            if (videoElement.paused) {
                videoElement.play();
            } else {
                videoElement.pause();
            }
        });
        
        // Initialize dashboard player if not already done
        if (!dashboardPlayer) {
            dashboardPlayer = new DashboardVideoPlayer('outputvideo');
        } else {
            dashboardPlayer.loadVideo(`/dashboard/media/serve/${key}`);
        }
    }

    window.listVideos = async function listVideos() {
        const medialist = document.getElementById('media-list');

        const res = await fetch('/api/ai/videos');
        const data = await res.json();
       

        // fill up medialist with data {"code":200,"msg":"success","data":{"videos":[{"taskId":"399d7d5178531f460ccb559c9054291c","videoBucketKey":"videos/399d7d5178531f460ccb559c9054291c.mp4","thumbnailBucketKey":"thumbnails/399d7d5178531f460ccb559c9054291c","videoUrl":"https://minio.azopsoft.com/fffcd269-61e0-463e-be76-7b93fc35aabd/videos/399d7d5178531f460ccb559c9054291c.mp4?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=9h3gUSzAplmdJUTzRCkU%2F20260323%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260323T175433Z&X-Amz-Expires=3600&X-Amz-SignedHeaders=host&X-Amz-Signature=3d647e05db31c818ff1ab9b44f4c992229733ea915cd2c9b586fe4edd57f560d","thumbnailUrl":"https://minio.azopsoft.com/fffcd269-61e0-463e-be76-7b93fc35aabd/thumbnails/399d7d5178531f460ccb559c9054291c?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=9h3gUSzAplmdJUTzRCkU%2F20260323%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260323T175433Z&X-Amz-Expires=3600&X-Amz-SignedHeaders=host&X-Amz-Signature=92603e55e56471796790adde205eb90a1184a594c4dc12fa2f46650e50cf3b9d","prompt":"Text-to-video prompt: Scene: ashen battlefield with monoliths. Subject: night hag ethereal. Camera/Motion: creature POV horror. Lighting & Mood: sickly green poison. Composition: clear subject, layered depth, natural lens behavior, subtle motion blur. Audio (optional): possessed speaking. Exclusions: friendly demon.","model":"grok-imagine/text-to-video","fileSize":2545713,"createdAt":"2026-03-23T17:15:15.031Z"}],"pagination":{"page":1,"limit":20,"total":1,"totalPages":1}}}
        medialist.innerHTML = ""

        for (const video of data.data.videos) {


            // http://localhost:3000/dashboard/media/serve/images/8463695b-38d0-49df-8968-4b87cae760f6.png

            const videoItem = document.createElement('div');
            videoItem.className = 'video-item';
            videoItem.dataset.videoKey = video.videoBucketKey;
            videoItem.dataset.videoName = (video.videoBucketKey || '').split('/').pop() || 'video.mp4';
            videoItem.draggable = true;

            const videoImg = document.createElement('img');
            videoImg.src = `/dashboard/media/serve/${video.thumbnailBucketKey}`;

            videoItem.appendChild(videoImg);

            // Add download button
            const downloadBtn = document.createElement('button');
            downloadBtn.className = 'video-item-download-btn';
            downloadBtn.innerHTML = '⬇';
            downloadBtn.title = 'Download Video';
            downloadBtn._videoKey = video.videoBucketKey;
            downloadBtn.addEventListener('click', (e) => {
                e.stopPropagation(); // Prevent triggering video load
                const a = document.createElement('a');
                a.href = `/dashboard/media/serve/${video.videoBucketKey}`;
                a.download = video.videoBucketKey || 'video.mp4';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            });
            videoItem.appendChild(downloadBtn);

            // "drag to bucket" hint badge
            const dragHint = document.createElement('div');
            dragHint.className = 'video-item-drag-hint';
            dragHint.textContent = 'drag → bucket';
            videoItem.appendChild(dragHint);

            // Drag to bucket — set the video key and thumbnail URL
            videoItem.addEventListener('dragstart', (e) => {
                const key = video.videoBucketKey;
                if (!key) return;
                e.dataTransfer.effectAllowed = 'copy';
                e.dataTransfer.setData('application/x-videogen-video', key);
                e.dataTransfer.setData('text/plain', videoItem.dataset.videoName);
                // Build a small 96x54 thumbnail canvas for the drag image
                const img = videoItem.querySelector('img');
                if (img && img.complete && img.naturalWidth > 0) {
                    try {
                        const c = document.createElement('canvas');
                        c.width = 96;
                        c.height = 54;
                        const ctx = c.getContext('2d');
                        ctx.drawImage(img, 0, 0, 96, 54);
                        e.dataTransfer.setDragImage(c, 48, 27);
                    } catch (_) {}
                }
            });

            medialist.appendChild(videoItem);

            videoItem.addEventListener('click', () => {
                // will open into


                loadVideo(video.videoBucketKey);

            });


        }


    }

    setTimeout(() => {
        listVideos();
    }, 1000);

});

// ----------------------------------------------------------------------------
// Bucket integration — drag generated videos to bucket
// ----------------------------------------------------------------------------
const _videogenBucket = initLegacyBucket({
    appName: 'videogen',
    onBucketDrop: (e) => {
        // Check for video item drag from media-list
        const videoKey = e.dataTransfer.getData('application/x-videogen-video')
        if (videoKey && _videogenBucket.bucket) {
            const name = (e.dataTransfer.getData('text/plain') || videoKey.split('/').pop() || `videogen_${Date.now()}.mp4`)
            _videogenBucket.bucket.setStatus(`Fetching ${name}...`)
            // Fetch the video and add to bucket
            fetch(`/dashboard/media/serve/${videoKey}`)
                .then(r => r.blob())
                .then(blob => blob.arrayBuffer())
                .then(data => {
                    _videogenBucket.bucket.add({
                        source: 'videogen',
                        name,
                        videoData: data,
                        videoFormat: 'mp4',
                        fileType: 'video'
                    }).catch(() => {})
                })
                .catch(err => {
                    _videogenBucket.bucket.setStatus('Failed to fetch video')
                })
            return
        }
    },
})

// Make video items in media-list draggable to bucket — dragstart is now set
// directly on each video-item in listVideos(), so no patching needed.